import React, { useEffect, useMemo, useState } from 'react';
import { Polygon, Polyline, useGoogleMap } from '@react-google-maps/api';
import {
  CAP_ANTENNA_BEAMWIDTH_DEG,
  CAP_DIRECTION_COLOR,
  CAP_WEDGE_LENGTH_PX,
  capWedgeLatLngs,
  capWedgeStyle,
  metersPerPixel,
} from '../../utils/capMarkerUtils';

/**
 * Draws each CAP's antenna coverage (80° sector) around its estimated
 * direction as a ground-anchored wedge.
 * Being map geometry, it rotates with the map heading. It tracks zoom
 * itself to keep a constant on-screen length, so zooming re-renders only
 * this layer, not the parent's markers.
 *
 * @param {{caps: Array<{serial:string, lat:number, lng:number, azimuth:object}>}} props
 */
export default function CapDirectionWedges({ caps }) {
  const map = useGoogleMap();
  const [zoom, setZoom] = useState(() => map?.getZoom?.() ?? 15);

  useEffect(() => {
    if (!map) return undefined;
    setZoom(map.getZoom());
    const listener = map.addListener('zoom_changed', () => setZoom(map.getZoom()));
    return () => listener.remove();
  }, [map]);

  const computeOffset = window.google?.maps?.geometry?.spherical?.computeOffset;

  const wedges = useMemo(() => {
    if (!computeOffset || !Array.isArray(caps)) return [];
    const offset = (from, distance, heading) => {
      const p = computeOffset(from, distance, heading);
      return { lat: p.lat(), lng: p.lng() };
    };
    return caps.map(({ serial, lat, lng, azimuth }) => {
      const center = { lat, lng };
      const radiusM = CAP_WEDGE_LENGTH_PX * metersPerPixel(lat, zoom);
      return {
        serial,
        style: capWedgeStyle(azimuth),
        paths: capWedgeLatLngs(center, azimuth.azimuthDeg, CAP_ANTENNA_BEAMWIDTH_DEG / 2, radiusM, offset),
        boresight: [center, offset(center, radiusM, azimuth.azimuthDeg)],
      };
    });
  }, [caps, zoom, computeOffset]);

  return wedges.map(({ serial, style, paths, boresight }) => (
    <React.Fragment key={`cap-dir-${serial}`}>
      <Polygon paths={paths} options={style.polygon} />
      <Polyline
        path={boresight}
        options={style.boresightDashed
          ? {
            clickable: false,
            strokeOpacity: 0,
            zIndex: 51,
            icons: [{
              icon: { path: 'M 0,-1 0,1', strokeOpacity: 0.9, strokeColor: CAP_DIRECTION_COLOR, scale: 2 },
              offset: '0',
              repeat: '8px',
            }],
          }
          : { clickable: false, strokeColor: CAP_DIRECTION_COLOR, strokeOpacity: 0.95, strokeWeight: 2, zIndex: 51 }}
      />
    </React.Fragment>
  ));
}
